import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Calendar, Search, CheckCircle2, XCircle, Clock, MapPin, ScanFace, AlertTriangle, Filter, Users, Pencil, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Card, PageHeader, Button, Badge, Field, Input, Select, Modal, Textarea } from "@/components/ui-kit";
import { usePagination } from "@/hooks/usePagination";
import { useAuth } from "@/contexts/AuthContext";
import { DatePicker } from "@/components/DatePicker";

/**
 * Iter v29 — Admin/HR Absensi review portal.
 * Default mode = "month" (bulan berjalan). Tabs: Bulan Ini · Hari Ini · Rentang Custom.
 * Filter by employee + department category. Approve / Reject pending_approval rows.
 * Iter v34j (#6) — Edit + Delete waktu absensi (admin/HR only).
 */

const todayStr = () => new Date().toISOString().slice(0, 10);

// Convert ISO UTC → local "datetime-local" string (yyyy-MM-ddTHH:mm) in browser tz.
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
};

// Convert "datetime-local" string back to ISO with timezone (assumes local browser TZ).
const localInputToIso = (s) => {
  if (!s) return null;
  try { return new Date(s).toISOString(); } catch { return null; }
};

export default function AbsensiAdmin() {
  const [mode, setMode] = useState("month"); // month | today | range
  const [date, setDate] = useState(todayStr());
  const [fromDate, setFromDate] = useState(todayStr().slice(0, 8) + "01");
  const [toDate, setToDate] = useState(todayStr());
  const [employeeId, setEmployeeId] = useState("");
  const [category, setCategory] = useState("");
  const [data, setData] = useState({ rows: [], summary: {} });
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  // Iter v34j (#6) — Edit/Delete state
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState({ clock_in: "", clock_out: "", note: "" });
  // Iter 64 (#6) — perm split: hide edit/delete buttons if user lacks perm.
  const { hasPerm } = useAuth();

  const loadRefs = async () => {
    try {
      const [e, c] = await Promise.all([api.get("/employees"), api.get("/department-categories")]);
      setEmployees(e.data); setCategories(c.data);
    } catch { /* fallback empty */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { mode };
      if (mode === "today") params.date = date;
      if (mode === "month") params.date = date;
      if (mode === "range") { params.from_date = fromDate; params.to_date = toDate; }
      if (employeeId) params.employee_id = employeeId;
      if (category) params.department_category = category;
      const r = await api.get("/attendance/admin", { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat data");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode, date, fromDate, toDate, employeeId, category]);

  const filteredRows = data.rows || [];
  const { pageItems, Pagination } = usePagination(filteredRows, { perPage: 25 });

  const approve = async (att) => {
    if (!window.confirm(`Setujui absensi ${att.employee_name} (${att.date})?`)) return;
    try {
      await api.post(`/attendance/${att.id}/approve`);
      toast.success("Absensi disetujui");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const doReject = async () => {
    if (!rejectFor) return;
    try {
      await api.post(`/attendance/${rejectFor.id}/reject`, { note: rejectNote });
      toast.success("Absensi ditolak");
      setRejectFor(null); setRejectNote("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  // Iter v34j (#6) — Edit + Delete handlers
  const openEdit = (att) => {
    setEditFor(att);
    // Iter v34{ak} — When clock_out is empty (legacy record with status=completed
    // but no clock_out_ts), prefill the datetime-local with the SAME DATE as
    // clock_in (default time 17:00 local). This prevents users from silently
    // typing only "00:00" which fails HTML5 validation and gets sent as null.
    let clockOutValue = isoToLocalInput(att.clock_out_ts);
    if (!clockOutValue && att.clock_in_ts) {
      // Extract just the date part from clock_in_local_ts (preferred) or clock_in_ts
      const src = att.clock_in_local_ts || att.clock_in_ts;
      const d = new Date(src);
      const pad = (n) => String(n).padStart(2, "0");
      clockOutValue = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T17:00`;
    }
    setEditForm({
      clock_in: isoToLocalInput(att.clock_in_ts),
      clock_out: clockOutValue,
      note: "",
    });
  };
  const saveEdit = async () => {
    if (!editFor) return;
    // Iter v34{ak} — Guard: reject partial/invalid datetime-local values so users
    // get a clear error instead of a silent null persist.
    const inISO = editForm.clock_in ? localInputToIso(editForm.clock_in) : null;
    const outISO = editForm.clock_out ? localInputToIso(editForm.clock_out) : null;
    if (editForm.clock_in && !inISO) {
      toast.error("Jam masuk tidak valid. Format: YYYY-MM-DD HH:MM.");
      return;
    }
    if (editForm.clock_out && !outISO) {
      toast.error("Jam keluar tidak valid. Format: YYYY-MM-DD HH:MM.");
      return;
    }
    // Sanity check: clock_out must be AFTER clock_in.
    if (inISO && outISO && new Date(outISO) <= new Date(inISO)) {
      toast.error("Jam keluar harus setelah jam masuk. Cek tanggal & jam.");
      return;
    }
    const payload = {
      clock_in_ts: inISO,
      clock_out_ts: outISO,
      note: editForm.note,
    };
    try {
      const resp = await api.patch(`/attendance/${editFor.id}`, payload);
      // Iter v34{ak} — Verify server actually persisted clock_out_ts (paranoid check).
      if (payload.clock_out_ts && !resp?.data?.clock_out_ts) {
        toast.warning("Server tidak mengembalikan jam keluar. Cek log admin.");
      }
      toast.success("Absensi diperbarui");
      setEditFor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
  };
  const doDelete = async (att) => {
    if (!window.confirm(`Hapus record absensi ${att.employee_name} (${att.date})? Tindakan ini tidak dapat dibatalkan.`)) return;
    try {
      await api.delete(`/attendance/${att.id}`);
      toast.success("Absensi dihapus");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus"); }
  };

  // Iter v34au (#1) — Force recompute overtime for a single record. Useful for
  // older records whose overtime_hours=0 even though work_hours>standard.
  const doRecompute = async () => {
    if (!editFor) return;
    if (!window.confirm("Hitung ulang Jam Kerja & Lembur dari waktu clock-in/out?\n\nBerguna untuk record lama yang overtime tercatat 0 padahal jam kerja > 8j.")) return;
    try {
      const r = await api.post(`/attendance/${editFor.id}/recompute`);
      const { before, after } = r.data || {};
      toast.success(`Recompute selesai: WH ${before?.work_hours ?? "?"}→${after?.work_hours ?? "?"} · OT ${before?.overtime_hours ?? "?"}→${after?.overtime_hours ?? "?"}`);
      setEditFor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal recompute"); }
  };

  // Iter v34am — Reset & Izinkan Absen Ulang. Menghapus record aktif sehingga
  // karyawan dapat clock-in kembali di hari yang sama (mis: salah tap, GPS
  // error, atau butuh koreksi total). Berbeda dengan Simpan yang hanya
  // mengedit waktu; ini benar-benar menghapus baris.
  const doResetForReclockIn = async () => {
    if (!editFor) return;
    if (!window.confirm(
      `RESET & Izinkan Absen Ulang untuk ${editFor.employee_name} (${editFor.date})?\n\n` +
      `Baris absensi ini akan DIHAPUS sehingga karyawan bisa clock-in lagi.\n\n` +
      `Tindakan ini tidak dapat dibatalkan.`
    )) return;
    try {
      await api.delete(`/attendance/${editFor.id}`);
      toast.success("Record dihapus. Karyawan dapat clock-in kembali.");
      setEditFor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal reset"); }
  };

  const fmtTime = (ts) => ts ? ts.slice(11, 16) : "—";
  const fmtDate = (d) => {
    try { const x = new Date(d + "T00:00"); return x.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); } catch { return d; }
  };

  const periodLabel = useMemo(() => {
    if (mode === "today") return fmtDate(date);
    if (mode === "month") return new Date(date + "T00:00").toLocaleDateString("id-ID", { month: "long", year: "numeric" });
    if (mode === "range") return `${fmtDate(fromDate)} – ${fmtDate(toDate)}`;
    return "";
  }, [mode, date, fromDate, toDate]);

  return (
    <div className="space-y-6" data-testid="absensi-admin-page">
      <PageHeader overline="Admin · HR" title="Absensi Karyawan" description="Pantau, setujui, dan tolak absensi seluruh karyawan." />

      {/* Filter bar */}
      <Card className="p-4 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex border border-stone-300" data-testid="absensi-mode-tabs">
            {[
              { k: "month", label: "Bulan Ini" },
              { k: "today", label: "Hari Ini" },
              { k: "range", label: "Rentang Kustom" },
            ].map((t) => (
              <button key={t.k} onClick={() => setMode(t.k)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider ${mode === t.k ? "bg-terra-500 text-white" : "bg-white text-stone-700 hover:bg-stone-50"}`}
                data-testid={`absensi-tab-${t.k}`}>
                {t.label}
              </button>
            ))}
          </div>
          <Badge tone="stone"><Calendar className="h-3 w-3 inline" /> {periodLabel}</Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {mode === "today" && (
            <Field label="Tanggal">
              <DatePicker value={date} onChange={(e) => setDate(e.target.value)} data-testid="absensi-date" />
            </Field>
          )}
          {mode === "month" && (
            <Field label="Bulan (pilih tanggal apapun pada bulan tsb)">
              <Input type="month" value={date.slice(0, 7)} onChange={(e) => setDate(e.target.value + "-15")} data-testid="absensi-month" />
            </Field>
          )}
          {mode === "range" && (
            <>
              <Field label="Dari"><DatePicker value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="absensi-from" /></Field>
              <Field label="Sampai"><DatePicker value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="absensi-to" /></Field>
            </>
          )}
          <Field label="Karyawan">
            <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} data-testid="absensi-emp-filter">
              <option value="">— Semua —</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </Select>
          </Field>
          <Field label="Kategori Departemen">
            <Select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="absensi-cat-filter">
              <option value="">— Semua —</option>
              {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </Select>
          </Field>
        </div>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="p-3 text-center"><div className="text-[10px] uppercase tracking-widest text-stone-500">Total Record</div><div className="text-2xl font-display font-bold text-stone-900 mt-1" data-testid="absensi-stat-total">{data.summary?.total || 0}</div></Card>
        <Card className="p-3 text-center"><div className="text-[10px] uppercase tracking-widest text-stone-500">Selesai</div><div className="text-2xl font-display font-bold text-emerald-700 mt-1" data-testid="absensi-stat-done">{data.summary?.completed || 0}</div></Card>
        <Card className="p-3 text-center"><div className="text-[10px] uppercase tracking-widest text-stone-500">Pending</div><div className="text-2xl font-display font-bold text-amber-700 mt-1" data-testid="absensi-stat-pending">{data.summary?.pending_approval || 0}</div></Card>
        <Card className="p-3 text-center"><div className="text-[10px] uppercase tracking-widest text-stone-500">Total Jam Kerja</div><div className="text-2xl font-display font-bold text-terra-700 mt-1">{(data.summary?.total_work_hours || 0).toFixed(1)}j</div></Card>
        <Card className="p-3 text-center"><div className="text-[10px] uppercase tracking-widest text-stone-500">Total Lembur</div><div className="text-2xl font-display font-bold text-stone-700 mt-1">{(data.summary?.total_overtime_hours || 0).toFixed(1)}j</div></Card>
      </div>

      {/* Table */}
      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
            <tr>
              <th className="text-left px-3 py-3">Tanggal</th>
              <th className="text-left px-3 py-3">Karyawan</th>
              <th className="text-left px-3 py-3">Departemen</th>
              <th className="text-center px-3 py-3">Jam Masuk</th>
              <th className="text-center px-3 py-3">Jam Keluar</th>
              <th className="text-right px-3 py-3">Jarak (m)</th>
              <th className="text-right px-3 py-3">Jam Kerja</th>
              <th className="text-right px-3 py-3">Lembur</th>
              <th className="text-center px-3 py-3">Flag</th>
              <th className="text-center px-3 py-3">Status</th>
              <th className="text-right px-3 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {loading && (
              <tr><td colSpan={11} className="px-3 py-12 text-center text-xs text-stone-400">Memuat…</td></tr>
            )}
            {!loading && pageItems.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-12 text-center text-xs text-stone-400">Belum ada data absensi pada periode ini.</td></tr>
            )}
            {!loading && pageItems.map((r) => {
              const inFlag = r.clock_in_face_override || !r.clock_in_inside_radius;
              const outFlag = r.clock_out_face_override || (r.clock_out_ts && !r.clock_out_inside_radius);
              return (
                <tr key={r.id} className="hover:bg-stone-50/40" data-testid={`absensi-row-${r.id}`}>
                  <td className="px-3 py-2.5 font-mono text-xs text-stone-700">{fmtDate(r.date)}</td>
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-stone-900">{r.employee_name}</div>
                    {r.position && <div className="text-[11px] text-stone-500">{r.position}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-stone-700">{r.department_category || "—"}</td>
                  <td className="px-3 py-2.5 text-center font-mono text-xs">{fmtTime(r.clock_in_local_ts || r.clock_in_ts)}</td>
                  <td className="px-3 py-2.5 text-center font-mono text-xs">{fmtTime(r.clock_out_local_ts || r.clock_out_ts)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs">
                    {/* Iter v34d (#3) — clickable distance opens Google Maps at clock-in coords */}
                    {r.clock_in_lat != null && r.clock_in_lng != null ? (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${r.clock_in_lat},${r.clock_in_lng}`}
                        target="_blank" rel="noreferrer"
                        className="text-terra-700 hover:text-terra-900 underline decoration-dotted inline-flex items-center gap-1"
                        title={`Lihat di Google Maps: ${r.clock_in_lat}, ${r.clock_in_lng}`}
                        data-testid={`absensi-map-in-${r.id}`}
                      >
                        <MapPin className="h-3 w-3" />{r.clock_in_distance_m || 0}m
                      </a>
                    ) : (
                      <div>{r.clock_in_distance_m || 0}m</div>
                    )}
                    {r.clock_out_distance_m != null && r.clock_out_lat != null && r.clock_out_lng != null ? (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${r.clock_out_lat},${r.clock_out_lng}`}
                        target="_blank" rel="noreferrer"
                        className="text-stone-500 hover:text-stone-800 underline decoration-dotted inline-flex items-center gap-1"
                        title={`Lihat di Google Maps: ${r.clock_out_lat}, ${r.clock_out_lng}`}
                        data-testid={`absensi-map-out-${r.id}`}
                      >
                        <MapPin className="h-3 w-3" />{r.clock_out_distance_m}m
                      </a>
                    ) : (
                      r.clock_out_distance_m != null && <div className="text-stone-400">{r.clock_out_distance_m}m</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs">{(r.work_hours || 0).toFixed(2)}j</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs">{(r.overtime_hours || 0).toFixed(2)}j</td>
                  <td className="px-3 py-2.5 text-center">
                    <div className="flex justify-center gap-1">
                      {r.clock_in_face_override && <span title="Override wajah saat masuk"><ScanFace className="h-3.5 w-3.5 text-amber-600" /></span>}
                      {!r.clock_in_inside_radius && <span title="Di luar radius saat masuk"><MapPin className="h-3.5 w-3.5 text-amber-600" /></span>}
                      {r.clock_out_face_override && <span title="Override wajah saat keluar"><ScanFace className="h-3.5 w-3.5 text-red-600" /></span>}
                      {r.clock_out_ts && !r.clock_out_inside_radius && <span title="Di luar radius saat keluar"><MapPin className="h-3.5 w-3.5 text-red-600" /></span>}
                      {!inFlag && !outFlag && <span className="text-stone-300">—</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <Badge tone={r.status === "completed" ? "emerald" : (r.status === "pending_approval" ? "amber" : (r.status === "rejected" ? "red" : "stone"))}>
                      {r.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {r.status === "pending_approval" && (
                      <>
                        <button onClick={() => approve(r)} className="text-emerald-600 hover:text-emerald-800 px-2" title="Setujui" data-testid={`absensi-approve-${r.id}`}>
                          <CheckCircle2 className="h-4 w-4" />
                        </button>
                        <button onClick={() => { setRejectFor(r); setRejectNote(""); }} className="text-red-600 hover:text-red-800 px-2" title="Tolak" data-testid={`absensi-reject-${r.id}`}>
                          <XCircle className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    {hasPerm("attendance.edit") && (
                      <button onClick={() => openEdit(r)} className="text-stone-500 hover:text-terra-700 px-2" title="Edit Waktu" data-testid={`absensi-edit-${r.id}`}>
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                    {hasPerm("attendance.delete") && (
                      <button onClick={() => doDelete(r)} className="text-stone-500 hover:text-red-700 px-2" title="Hapus" data-testid={`absensi-delete-${r.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {Pagination}
      </Card>

      {/* Reject modal */}
      <Modal open={!!rejectFor} onClose={() => setRejectFor(null)} title="Tolak Absensi" size="sm">
        {rejectFor && (
          <div className="space-y-3" data-testid="absensi-reject-modal">
            <p className="text-xs text-stone-600">Tolak absensi <b>{rejectFor.employee_name}</b> tanggal <b>{fmtDate(rejectFor.date)}</b>?</p>
            <Field label="Alasan (opsional)">
              <Textarea rows={3} value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="Misal: lokasi tidak valid, foto KTP tidak jelas..." data-testid="absensi-reject-note" />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setRejectFor(null)}>Batal</Button>
              <Button onClick={doReject} className="!bg-red-600 hover:!bg-red-700" data-testid="absensi-reject-confirm">Tolak</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Iter v34j (#6) — Edit attendance time modal */}
      <Modal open={!!editFor} onClose={() => setEditFor(null)} title="Edit Waktu Absensi" size="md">
        {editFor && (
          <div className="space-y-3" data-testid="absensi-edit-modal">
            <div className="bg-stone-50 border border-stone-200 p-3 text-sm">
              <p className="font-medium">{editFor.employee_name}</p>
              <p className="text-stone-500 text-xs font-mono">{fmtDate(editFor.date)}</p>
            </div>
            <Field label="Jam Masuk (lokal browser)">
              <Input type="datetime-local" value={editForm.clock_in} onChange={(e) => setEditForm({ ...editForm, clock_in: e.target.value })} data-testid="absensi-edit-in" />
            </Field>
            <Field label="Jam Keluar (lokal browser, kosongkan jika belum keluar)">
              <div className="flex items-center gap-2">
                <Input type="datetime-local" value={editForm.clock_out} onChange={(e) => setEditForm({ ...editForm, clock_out: e.target.value })} data-testid="absensi-edit-out" />
                {editForm.clock_out && (
                  <button
                    type="button"
                    onClick={() => setEditForm({ ...editForm, clock_out: "" })}
                    className="text-xs text-stone-500 hover:text-red-700 whitespace-nowrap"
                    data-testid="absensi-edit-out-clear"
                    title="Kosongkan jam keluar (revert ke IN_PROGRESS)"
                  >
                    Kosongkan
                  </button>
                )}
              </div>
            </Field>
            <Field label="Catatan Koreksi (opsional)">
              <Textarea rows={2} value={editForm.note} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} placeholder="Mis: salah ketuk, internet putus saat absen pulang…" data-testid="absensi-edit-note" />
            </Field>
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 p-2"><AlertTriangle className="h-3 w-3 inline" /> Perubahan akan tercatat di log sistem dan jam kerja / lembur akan dihitung ulang.</p>
            <div className="flex flex-wrap justify-between gap-2 pt-2">
              {hasPerm("attendance.delete") ? (
                <Button variant="outline" onClick={doResetForReclockIn}
                  className="!text-red-700 !border-red-300 hover:!bg-red-50"
                  data-testid="absensi-edit-reset-reclock">
                  <Trash2 className="h-3.5 w-3.5" />Reset & Izinkan Absen Ulang
                </Button>
              ) : <span />}
              <div className="flex gap-2 flex-wrap">
                {hasPerm("attendance.edit") && (
                  <Button variant="outline" onClick={doRecompute}
                    className="!text-blue-700 !border-blue-300 hover:!bg-blue-50"
                    data-testid="absensi-edit-recompute"
                    title="Hitung ulang jam kerja & lembur dari clock-in/out">
                    Hitung Ulang WH/OT
                  </Button>
                )}
                <Button variant="outline" onClick={() => setEditFor(null)}>Batal</Button>
                <Button onClick={saveEdit} data-testid="absensi-edit-save">Simpan</Button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
